import {ChangeEvent, DragEvent, FC, useRef, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {numberWithCommas} from '../../../../../../../_metronic/helpers'
import {
  confirmTaxFolderPdf,
  previewTaxFolderPdf,
  TaxFolderPdfConfirmResult,
  TaxFolderPdfPreviewResult,
} from '../../core/_requests'
import './TaxFolderImportModal.css'
import './TaxFolderPdfImportModal.css'

type Props = {
  onClose: () => void
  onImported: (result: TaxFolderPdfConfirmResult) => void
}

const MAX_FILE_SIZE = 15 * 1024 * 1024

const getRequestError = (error: any) => {
  const data = error?.response?.data
  if (typeof data === 'string' && data.trim()) {
    return data
  }
  return (
    data?.Message ||
    data?.message ||
    error?.message ||
    'پردازش PDF صورتحساب انجام نشد. لطفاً دوباره تلاش کنید.'
  )
}

const TaxFolderPdfImportModal: FC<Props> = ({onClose, onImported}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File>()
  const [preview, setPreview] = useState<TaxFolderPdfPreviewResult>()
  const [previewLoading, setPreviewLoading] = useState(false)
  const [confirmLoading, setConfirmLoading] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  const [approved, setApproved] = useState(false)
  const [error, setError] = useState('')
  const loading = previewLoading || confirmLoading

  const selectFile = (nextFile?: File) => {
    setError('')
    setPreview(undefined)
    setApproved(false)
    if (!nextFile) {
      setFile(undefined)
      return
    }
    if (!nextFile.name.toLowerCase().endsWith('.pdf')) {
      setFile(undefined)
      setError('فقط PDF تکی صورتحساب دریافت‌شده از کارپوشه قابل انتخاب است.')
      return
    }
    if (nextFile.size > MAX_FILE_SIZE) {
      setFile(undefined)
      setError('حجم فایل نباید بیشتر از ۱۵ مگابایت باشد.')
      return
    }
    setFile(nextFile)
  }

  const reset = () => {
    selectFile(undefined)
    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }

  const upload = () => {
    if (!file || loading) return
    setPreviewLoading(true)
    setError('')
    previewTaxFolderPdf(file)
      .then(setPreview)
      .catch((requestError) => setError(getRequestError(requestError)))
      .finally(() => setPreviewLoading(false))
  }

  const confirm = () => {
    if (!preview || !approved || loading) return
    setConfirmLoading(true)
    setError('')
    confirmTaxFolderPdf(preview.token)
      .then(onImported)
      .catch((requestError) => setError(getRequestError(requestError)))
      .finally(() => setConfirmLoading(false))
  }

  const invoice = preview?.invoice

  return (
    <Modal
      show
      onHide={() => !loading && onClose()}
      centered
      size='xl'
      fullscreen='sm-down'
      backdrop={loading ? 'static' : true}
      dialogClassName='mp-tax-folder-modal mp-tax-folder-pdf-modal'
    >
      <Modal.Header>
        <div className='d-flex align-items-center gap-3 min-w-0'>
          <span className='mp-tax-folder-modal__icon is-pdf' aria-hidden='true'>
            <i className='las la-file-pdf'></i>
          </span>
          <div className='min-w-0'>
            <Modal.Title as='h2'>ورود PDF تکی صورتحساب کارپوشه</Modal.Title>
            <div className='text-gray-500 fs-8 mt-1'>
              استخراج مشخصات و اقلام، بازبینی و ثبت در مرحله ارسال و استعلام موفق
            </div>
          </div>
        </div>
        <button
          type='button'
          className='btn btn-sm btn-icon btn-light'
          aria-label='بستن'
          disabled={loading}
          onClick={onClose}
        >
          <i className='las la-times fs-2'></i>
        </button>
      </Modal.Header>

      <Modal.Body>
        {!preview && (
          <>
            <div className='mp-tax-folder-modal__notice'>
              <i className='las la-shield-alt' aria-hidden='true'></i>
              <div>
                <strong>PDF جزئیات کامل یک صورتحساب را مستقیماً از کارپوشه انتخاب کنید.</strong>
                <span>
                  ابتدا اطلاعات و تمام اقلام نمایش داده می‌شود و تا پیش از تأیید شما چیزی در پنل ثبت
                  نخواهد شد.
                </span>
              </div>
            </div>
            <div
              className={`mp-tax-folder-dropzone ${dragActive ? 'is-active' : ''} ${
                file ? 'has-file' : ''
              }`}
              onDragEnter={(event) => {
                event.preventDefault()
                setDragActive(true)
              }}
              onDragOver={(event) => event.preventDefault()}
              onDragLeave={() => setDragActive(false)}
              onDrop={(event: DragEvent<HTMLDivElement>) => {
                event.preventDefault()
                setDragActive(false)
                selectFile(event.dataTransfer.files?.[0])
              }}
            >
              <input
                ref={inputRef}
                id='tax-folder-pdf-file'
                type='file'
                accept='.pdf,application/pdf'
                disabled={loading}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  selectFile(event.target.files?.[0])
                }
              />
              <label htmlFor='tax-folder-pdf-file'>
                <i className={`las ${file ? 'la-file-pdf' : 'la-cloud-upload-alt'}`}></i>
                <strong>{file ? file.name : 'PDF صورتحساب را انتخاب کنید'}</strong>
                <span>
                  {file
                    ? `${numberWithCommas(Math.max(Math.ceil(file.size / 1024), 1))} کیلوبایت`
                    : 'یا فایل را اینجا رها کنید — حداکثر ۱۵ مگابایت'}
                </span>
              </label>
              {file && (
                <button type='button' className='btn btn-sm btn-light-danger' onClick={reset}>
                  حذف فایل
                </button>
              )}
            </div>
          </>
        )}

        {error && (
          <div className='alert alert-danger d-flex align-items-start gap-2 mt-4 mb-0' role='alert'>
            <i className='las la-exclamation-circle fs-2 mt-1'></i>
            <span>{error}</span>
          </div>
        )}

        {invoice && (
          <div className='mp-tax-folder-pdf-preview'>
            <div
              className={`mp-tax-folder-pdf-preview__status ${
                preview?.hasDiscrepancy
                  ? 'is-discrepancy'
                  : preview?.alreadyExists
                  ? 'is-existing'
                  : ''
              }`}
            >
              <i
                className={`las ${
                  preview?.hasDiscrepancy
                    ? 'la-exclamation-triangle'
                    : preview?.alreadyExists
                    ? 'la-info-circle'
                    : 'la-check-circle'
                }`}
              ></i>
              <div>
                <strong>
                  {preview?.hasDiscrepancy
                    ? 'این صورتحساب با اطلاعات کارپوشه مغایرت دارد.'
                    : preview?.alreadyExists
                    ? 'این صورتحساب قبلاً در پنل ثبت شده است.'
                    : 'اطلاعات PDF با موفقیت استخراج شد.'}
                </strong>
                <span>
                  {preview?.hasDiscrepancy
                    ? 'خروجی کارپوشه ملاک نهایی است. اختلاف‌ها را بررسی کنید؛ فقط پس از تأیید شما یک نسخه ویرایش‌شده خودکار ایجاد می‌شود.'
                    : preview?.alreadyExists
                    ? 'اطلاعات پنل و کارپوشه یکسان است؛ با تأیید، عملیات ورود و PDF اصلی در لاگ همین صورتحساب ثبت می‌شود.'
                    : 'مشخصات و اقلام زیر را کنترل کنید؛ با تأیید، یک صورتحساب کامل واقعاً در پنل ثبت می‌شود.'}
                </span>
              </div>
            </div>

            <div className='mp-tax-folder-pdf-preview__ownership'>
              <i className='las la-shield-alt'></i>
              <span>
                <strong>تعلق PDF به کسب‌وکار تأیید شد.</strong> اطلاعات فروشنده و ۶ کاراکتر نخست
                شماره مالیاتی با شناسه یکتای حافظه مالیاتی ثبت‌شده در تنظیمات مطابقت دارد.
              </span>
            </div>

            {preview?.differences?.length > 0 && (
              <div className='mp-tax-folder-pdf-preview__differences'>
                <div className='mp-tax-folder-pdf-preview__differences-head'>
                  <strong>مغایرت‌های شناسایی‌شده</strong>
                  <span>{numberWithCommas(preview.differences.length)} مورد</span>
                </div>
                <div className='mp-tax-folder-pdf-preview__differences-list'>
                  {preview.differences.map((difference, index) => (
                    <div key={`${difference.field}-${index}`}>
                      <strong>{difference.field}</strong>
                      <span>
                        <small>پنل</small>
                        {difference.localValue || '—'}
                      </span>
                      <i className='las la-long-arrow-alt-left'></i>
                      <span>
                        <small>کارپوشه</small>
                        {difference.folderValue || '—'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className='mp-tax-folder-pdf-preview__identity'>
              <div>
                <span>شناسه مالیاتی</span>
                <strong className='englishText'>{invoice.TaxId}</strong>
              </div>
              <div>
                <span>شماره صورتحساب</span>
                <strong className='englishText'>{invoice.No}</strong>
              </div>
              <div>
                <span>تاریخ و ساعت صدور</span>
                <strong>{invoice.Date}</strong>
              </div>
              <div>
                <span>موضوع / الگو</span>
                <strong>
                  {invoice.SubjectTitle} · {invoice.TemplateTitle}
                </strong>
              </div>
              <div>
                <span>نوع / روش تسویه</span>
                <strong>
                  نوع {numberWithCommas(invoice.Type)} · {invoice.SettlementTitle}
                </strong>
              </div>
              <div>
                <span>وضعیت کارپوشه</span>
                <strong>{invoice.FolderStatus || 'نامشخص'}</strong>
              </div>
            </div>

            <div className='mp-tax-folder-pdf-preview__parties'>
              <article>
                <span>فروشنده</span>
                <strong>{invoice.SellerName || '-'}</strong>
                <small className='englishText'>
                  {invoice.SellerNationalCode || invoice.SellerEconomicCode || '-'}
                </small>
              </article>
              <i className='las la-long-arrow-alt-left'></i>
              <article>
                <span>خریدار</span>
                <strong>{invoice.BuyerName || '-'}</strong>
                <small className='englishText'>
                  {invoice.BuyerNationalCode || invoice.BuyerEconomicCode || '-'}
                </small>
              </article>
            </div>

            <div className='mp-tax-folder-pdf-preview__table-wrap'>
              <div className='mp-tax-folder-pdf-preview__table-head'>
                <div>
                  <strong>اقلام صورتحساب</strong>
                  <span>{numberWithCommas(invoice.Items.length)} ردیف استخراج‌شده</span>
                </div>
                <strong>{numberWithCommas(invoice.NetPrice)} ریال</strong>
              </div>
              <div className='table-responsive'>
                <table className='table align-middle mb-0'>
                  <thead>
                    <tr>
                      <th>ردیف</th>
                      <th>شناسه کالا/خدمت</th>
                      <th>شرح</th>
                      <th>واحد</th>
                      <th>تعداد</th>
                      <th>مبلغ واحد</th>
                      <th>تخفیف</th>
                      <th>مالیات</th>
                      <th>مبلغ نهایی</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.Items.map((item) => (
                      <tr key={`${item.RowNumber}-${item.StuffId}`}>
                        <td>{numberWithCommas(item.RowNumber)}</td>
                        <td className='englishText'>{item.StuffId}</td>
                        <td>{item.Description}</td>
                        <td>{item.Unit}</td>
                        <td>{numberWithCommas(item.Quantity)}</td>
                        <td>{numberWithCommas(item.UnitPrice)}</td>
                        <td>{numberWithCommas(item.Discount)}</td>
                        <td>{numberWithCommas(item.VatPrice + item.OtherTaxPrice)}</td>
                        <td className='fw-bolder'>{numberWithCommas(item.TotalPrice)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {invoice.Warnings?.length > 0 && (
              <div className='mp-tax-folder-pdf-preview__warnings'>
                {invoice.Warnings.map((warning) => (
                  <div key={warning}>
                    <i className='las la-exclamation-triangle'></i>
                    <span>{warning}</span>
                  </div>
                ))}
              </div>
            )}

            <label className='mp-tax-folder-pdf-preview__approval'>
              <input
                type='checkbox'
                checked={approved}
                disabled={loading}
                onChange={(event) => setApproved(event.target.checked)}
              />
              <span>
                <strong>اطلاعات بالا را بررسی کردم و درست است.</strong>
                <small>
                  {preview?.alreadyExists
                    ? preview.hasDiscrepancy
                      ? 'خروجی کارپوشه ملاک نهایی باشد و صورتحساب به‌صورت خودکار مانند ویرایش، اصلاح شود.'
                      : 'ثبت مجدد تکراری انجام نشود و تأیید ورود همراه PDF اصلی در لاگ صورتحساب موجود ثبت شود.'
                    : 'صورتحساب با همین اطلاعات و تمام اقلام، واقعاً در پنل ثبت و PDF اصلی در لاگ نگهداری شود.'}
                </small>
              </span>
            </label>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <button type='button' className='btn btn-light' disabled={loading} onClick={onClose}>
          انصراف
        </button>
        {preview ? (
          <>
            <button
              type='button'
              className='btn btn-light-primary'
              disabled={loading}
              onClick={reset}
            >
              <i className='las la-redo fs-2'></i>انتخاب PDF دیگر
            </button>
            <button
              type='button'
              className='btn btn-primary'
              disabled={!approved || loading}
              onClick={confirm}
            >
              {confirmLoading ? (
                <>
                  <span className='spinner-border spinner-border-sm ms-2'></span>در حال ثبت...
                </>
              ) : (
                <>
                  <i className='las la-check fs-2'></i>
                  {preview.hasDiscrepancy
                    ? 'تأیید و اصلاح خودکار مغایرت‌ها'
                    : preview.alreadyExists
                    ? 'تأیید و ثبت عملیات ورود'
                    : 'تأیید و ثبت صورتحساب در پنل'}
                </>
              )}
            </button>
          </>
        ) : (
          <button
            type='button'
            className='btn btn-primary'
            disabled={!file || loading}
            onClick={upload}
          >
            {previewLoading ? (
              <>
                <span className='spinner-border spinner-border-sm ms-2'></span>در حال استخراج
                اطلاعات...
              </>
            ) : (
              <>
                <i className='las la-search fs-2'></i>پردازش و نمایش پیش‌نمایش
              </>
            )}
          </button>
        )}
      </Modal.Footer>
    </Modal>
  )
}

export {TaxFolderPdfImportModal}
